//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import {
  FacebookPageFeedLinkPost,
  FacebookPageFeedPhotoPost,
  FacebookPageFeedVideoPost,
  InstagramFeedAutomaticPost,
  InstagramFeedManualPost,
  InstagramReelsAutomaticPost,
  InstagramReelsManualPost,
  InstagramStoryGroupAutomaticPost,
  InstagramStoryGroupPost,
  MediaTweet,
  OrganicPin,
  ScheduledPostConfig,
  TikTokPhotoAutomaticPost,
  TikTokVideoAutomaticPost,
  YouTubeAutomaticPost,
} from '@bvengineering/universal-scheduling-gateway-service-types';

import Cuid from '@models/Cuid';
import PinterestBoardId from '@models/PinterestBoardId';
import { SocialNetwork } from '@models/SocialNetwork';
import UserId from '@models/UserId';
import { YouTubePlaylistId } from '@models/YouTubePlaylistId';
import { YouTubeVideoLicense } from '@models/YouTubeVideoLicense';
import { YouTubeVisibility } from '@models/YouTubeVisibility';
import { OmitStrict } from '@utils/types/OmitStrict';

export enum UniversalScheduledPostConfigType {
  FacebookPageFeedPhotoPost = 'v1:photo',
  FacebookPageFeedLinkPost = 'v1:link',
  FacebookPageFeedVideoPost = 'v1:video',
  InstagramFeedAutomaticPost = 'v1:automatic',
  InstagramFeedManualPost = 'v1:manual',
  InstagramReelsAutomaticPost = 'v1:instagram-reels-automatic',
  InstagramReelsManualPost = 'v1:instagram-reels',
  InstagramStoryGroupAutomaticPost = 'v1:instagram-story-group-automatic',
  InstagramStoryGroup = 'v1:instagram-story-group',
  PinterestOrganicPin = 'v1:organic',
  TikTokPhotoAutomaticPost = 'v1:tiktok-photo-automatic',
  TikTokVideoAutomaticPost = 'v1:tiktok-video-automatic',
  TwitterMediaPost = 'v1:media',
  YouTubeAutomaticPost = 'v1:youtube-automatic',
}

export type TScheduledPostConfig =
  | IFacebookPageFeedPostConfig
  | IInstagramFeedAutomaticPostConfig
  | IInstagramFeedManualPostConfig
  | IInstagramReelsAutomaticPostConfig
  | IInstagramReelsManualPostConfig
  | IInstagramStoryGroupAutomaticPostConfig
  | IInstagramStoryGroupPostConfig
  | IOrganicPinConfig
  | TikTokPhotoAutomaticPostConfig
  | ITikTokVideoAutomaticPostConfig
  | ITwitterMediaPostConfig
  | IYouTubeAutomaticPostConfig;

export interface IScheduledPostConfig
  extends OmitStrict<ScheduledPostConfig, '@type'> {
  '@type': UniversalScheduledPostConfigType;
  cuids: Cuid[]; // Missing from code-gen types, but actually present on all configs
  socialNetwork?: SocialNetwork;
}

export interface IOrganicPinConfig
  extends IScheduledPostConfig,
    OmitStrict<OrganicPin, 'boardId' | 'socialNetwork'> {
  '@type': UniversalScheduledPostConfigType.PinterestOrganicPin;
  boardId?: PinterestBoardId;
}

export interface ITwitterMediaPostConfig
  extends IScheduledPostConfig,
    OmitStrict<MediaTweet, 'socialNetwork'> {
  '@type': UniversalScheduledPostConfigType.TwitterMediaPost;
}

export interface IFacebookPageFeedPostConfig
  extends IScheduledPostConfig,
    OmitStrict<
      FacebookPageFeedPhotoPost &
        FacebookPageFeedLinkPost &
        FacebookPageFeedVideoPost,
      'socialNetwork'
    > {
  '@type':
    | UniversalScheduledPostConfigType.FacebookPageFeedPhotoPost
    | UniversalScheduledPostConfigType.FacebookPageFeedLinkPost
    | UniversalScheduledPostConfigType.FacebookPageFeedVideoPost;
}

export interface InstagramLocation {
  facebookLocationId: string;
  locationName: string;
  streetAddress?: string;
  city?: string;
}

export interface IInstagramFeedAutomaticPostConfig
  extends IScheduledPostConfig,
    OmitStrict<
      InstagramFeedAutomaticPost,
      | 'socialNetwork'
      | 'shouldCreateTwitterCrossPost'
      | 'shouldCreateFacebookCrossPost'
      | 'postType'
      | 'location'
    > {
  '@type': UniversalScheduledPostConfigType.InstagramFeedAutomaticPost;
  caption?: string; // this field was removed by code-gen in last update
  notifyUserId?: UserId;
  location?: InstagramLocation;
  shouldCreateFacebookCrossPost?: boolean;
  shouldCreateTwitterCrossPost?: boolean;
}

export interface IInstagramFeedManualPostConfig
  extends IScheduledPostConfig,
    OmitStrict<
      InstagramFeedManualPost,
      | 'socialNetwork'
      | 'shouldCreateTwitterCrossPost'
      | 'shouldCreateFacebookCrossPost'
      | 'postType'
    > {
  '@type': UniversalScheduledPostConfigType.InstagramFeedManualPost;
  caption?: string; // this field was removed by code-gen in last update
  notifyUserId?: UserId;
  shouldCreateFacebookCrossPost?: boolean;
  shouldCreateTwitterCrossPost?: boolean;
}

export interface IInstagramReelsAutomaticPostConfig
  extends IScheduledPostConfig,
    OmitStrict<InstagramReelsAutomaticPost, 'socialNetwork' | 'location'> {
  '@type': UniversalScheduledPostConfigType.InstagramReelsAutomaticPost;
  location?: InstagramLocation;
  notifyUserId?: UserId;
}

export interface IInstagramReelsManualPostConfig
  extends IScheduledPostConfig,
    OmitStrict<InstagramReelsManualPost, 'socialNetwork'> {
  '@type': UniversalScheduledPostConfigType.InstagramReelsManualPost;
  notifyUserId?: UserId;
}

export interface IInstagramStoryGroupAutomaticPostConfig
  extends IScheduledPostConfig,
    OmitStrict<InstagramStoryGroupAutomaticPost, 'socialNetwork'> {
  '@type': UniversalScheduledPostConfigType.InstagramStoryGroupAutomaticPost;
  notifyUserId?: UserId;
}

export interface IInstagramStoryGroupPostConfig
  extends IScheduledPostConfig,
    OmitStrict<InstagramStoryGroupPost, 'socialNetwork'> {
  '@type': UniversalScheduledPostConfigType.InstagramStoryGroup;
  notifyUserId?: UserId;
}

export interface TikTokPhotoAutomaticPostConfig
  extends IScheduledPostConfig,
    OmitStrict<
      TikTokPhotoAutomaticPost,
      | 'socialNetwork'
      | 'disableComment'
      | 'title'
      | 'autoAddMusic'
      | 'privacyLevel'
      | 'getPostType'
    > {
  '@type': UniversalScheduledPostConfigType.TikTokPhotoAutomaticPost;
  autoAddMusic: boolean;
  caption?: string; // this field was removed by code-gen in last update
  disableComment: boolean;
  title?: string;
}

export interface ITikTokVideoAutomaticPostConfig
  extends IScheduledPostConfig,
    OmitStrict<
      TikTokVideoAutomaticPost,
      | 'socialNetwork'
      | 'disableComment'
      | 'disableDuet'
      | 'disableStitch'
      | 'isAIGenerated'
      | 'thumbnailOffsetInMs'
      | 'getPostType'
    > {
  '@type': UniversalScheduledPostConfigType.TikTokVideoAutomaticPost;
  caption?: string; // this field was removed by code-gen in last update
  disableComment: boolean;
  disableDuet: boolean;
  disableStitch: boolean;
  isAIGenerated: boolean;
  thumbnailOffsetInMs?: number;
}

export interface IYouTubeAutomaticPostConfig
  extends IScheduledPostConfig,
    OmitStrict<
      YouTubeAutomaticPost,
      | 'allowEmbeds'
      | 'isSafeForKids'
      | 'license'
      | 'playlistIds'
      | 'privacy'
      | 'socialNetwork'
    > {
  '@type': UniversalScheduledPostConfigType.YouTubeAutomaticPost;
  allowEmbeds?: boolean;
  isSafeForKids?: boolean;
  license?: YouTubeVideoLicense;
  playlistIds?: YouTubePlaylistId[];
  privacy?: YouTubeVisibility;
}
